/**
 * Leitura de CSV de fatura de cartão, tolerante ao formato de bancos diferentes: delimitador (`,` ou `;`),
 * campos entre aspas com vírgula/aspas escapada dentro, decimal com vírgula, milhar com ponto, valor negativo
 * com espaço depois do sinal ("- 4.381,29"). Cópia idêntica de `shared/src/lib/csvFatura.ts` (usado pelo Mobo);
 * o Web não consome o pacote `shared`, então mantém a própria cópia — ver Mobo/README.md.
 */

export interface LinhaCsvFatura {
  descricao: string
  valor: number
  /** Data no formato ISO (YYYY-MM-DD). */
  data: string
}

export interface MapeamentoColunasCsv {
  dataIdx: number
  descricaoIdx: number
  valorIdx: number
}

/** Nomes de coluna reconhecidos automaticamente (minúsculo, sem acento), por campo. */
const NOMES_DATA = ['date', 'data']
const NOMES_DESCRICAO = ['title', 'descricao', 'descrição', 'historico', 'histórico', 'lancamento', 'lançamento', 'estabelecimento']
const NOMES_VALOR = ['amount', 'valor', 'valor (r$)', 'valor(r$)']

function normalizar(s: string): string {
  return s.trim().toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '')
}

/** Detecta o delimitador olhando a 1ª linha: o que aparece mais fora de aspas, entre vírgula e ponto e vírgula. */
function detectarDelimitador(primeiraLinha: string): ',' | ';' {
  let dentroAspas = false
  let virgulas = 0
  let pontoVirgulas = 0
  for (const ch of primeiraLinha) {
    if (ch === '"') dentroAspas = !dentroAspas
    else if (!dentroAspas && ch === ',') virgulas++
    else if (!dentroAspas && ch === ';') pontoVirgulas++
  }
  return pontoVirgulas > virgulas ? ';' : ','
}

/** Parser de uma linha CSV (RFC4180 simplificado): aspas escapadas como `""`, campos com delimitador dentro de aspas. */
function parseLinha(linha: string, delimitador: string): string[] {
  const campos: string[] = []
  let atual = ''
  let dentroAspas = false
  for (let i = 0; i < linha.length; i++) {
    const ch = linha[i]
    if (dentroAspas) {
      if (ch === '"') {
        if (linha[i + 1] === '"') { atual += '"'; i++ }
        else dentroAspas = false
      } else {
        atual += ch
      }
    } else if (ch === '"') {
      dentroAspas = true
    } else if (ch === delimitador) {
      campos.push(atual)
      atual = ''
    } else {
      atual += ch
    }
  }
  campos.push(atual)
  return campos
}

/** Quebra o texto em linhas respeitando `\n` dentro de aspas (raro, mas alguns bancos exportam assim). */
function quebrarLinhas(texto: string): string[] {
  const linhas: string[] = []
  let atual = ''
  let dentroAspas = false
  for (const ch of texto) {
    if (ch === '"') dentroAspas = !dentroAspas
    if ((ch === '\n' || ch === '\r') && !dentroAspas) {
      if (atual.trim().length > 0) linhas.push(atual)
      atual = ''
    } else if (!(ch === '\r')) {
      atual += ch
    }
  }
  if (atual.trim().length > 0) linhas.push(atual)
  return linhas
}

/** Todas as linhas do CSV já separadas em campos, incluindo o cabeçalho na posição 0. */
export function parseCsvBruto(texto: string): string[][] {
  const linhasTexto = quebrarLinhas(texto)
  if (linhasTexto.length === 0) return []
  const delimitador = detectarDelimitador(linhasTexto[0])
  return linhasTexto.map((l) => parseLinha(l, delimitador))
}

/** Tenta achar sozinho as 3 colunas necessárias pelo nome do cabeçalho. `null` no campo que não achou. */
export function detectarMapeamento(cabecalho: string[]): Partial<MapeamentoColunasCsv> {
  const normalizado = cabecalho.map(normalizar)
  const achar = (nomes: string[]) => {
    const i = normalizado.findIndex((c) => nomes.includes(c))
    return i >= 0 ? i : undefined
  }
  return { dataIdx: achar(NOMES_DATA), descricaoIdx: achar(NOMES_DESCRICAO), valorIdx: achar(NOMES_VALOR) }
}

export function mapeamentoCompleto(m: Partial<MapeamentoColunasCsv>): m is MapeamentoColunasCsv {
  return m.dataIdx !== undefined && m.descricaoIdx !== undefined && m.valorIdx !== undefined
}

/** "- 4.381,29" → -4381.29 · "4,00" → 4 · "1234.56" → 1234.56 (já com ponto decimal, sem milhar). */
export function parseValorCsv(bruto: string): number | null {
  const texto = bruto.trim()
  if (!texto) return null
  const negativo = /^-/.test(texto.replace(/\s/g, ''))
  const semSinal = texto.replace(/^[+-]\s*/, '').trim()
  const temVirgulaDecimal = /,\d{1,2}$/.test(semSinal)
  const numerico = temVirgulaDecimal
    ? semSinal.replace(/\./g, '').replace(',', '.')
    : semSinal.replace(/,/g, '')
  const valor = Number(numerico.replace(/[^\d.]/g, ''))
  if (Number.isNaN(valor)) return null
  return negativo ? -valor : valor
}

/** Aceita "2026-09-02" (ISO) ou "02/09/2026" / "02/09/26" (dia/mês/ano, formato comum no Brasil). */
export function parseDataCsv(bruto: string): string | null {
  const texto = bruto.trim()
  const iso = texto.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (iso) return `${iso[1]}-${iso[2]}-${iso[3]}`
  const br = texto.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/)
  if (br) {
    const [, d, mo, y] = br
    const ano = y.length === 2 ? `20${y}` : y
    return `${ano}-${mo.padStart(2, '0')}-${d.padStart(2, '0')}`
  }
  return null
}

/** Converte as linhas de dados (sem o cabeçalho) usando o mapeamento já resolvido. Ignora linhas onde
 * data ou valor não deram pra interpretar (reporta a contagem pro chamador decidir o que avisar). */
export function converterLinhas(
  linhasBrutas: string[][], mapeamento: MapeamentoColunasCsv,
): { linhas: LinhaCsvFatura[]; ignoradas: number } {
  const linhas: LinhaCsvFatura[] = []
  let ignoradas = 0
  for (const campos of linhasBrutas) {
    const valor = parseValorCsv(campos[mapeamento.valorIdx] ?? '')
    const data = parseDataCsv(campos[mapeamento.dataIdx] ?? '')
    const descricao = (campos[mapeamento.descricaoIdx] ?? '').trim()
    if (valor === null || data === null || !descricao) { ignoradas++; continue }
    linhas.push({ descricao, valor, data })
  }
  return { linhas, ignoradas }
}
